import { api } from '@/lib/api'
import { API_BASE_URL, API_ENDPOINTS } from '@/lib/config'
import { createCoalescedFetcher } from '@/lib/requestCoalesce'
import {
  getInterviewEntryFlow,
  setInterviewEntryFlow,
  type InterviewEntryFlow,
} from '@/lib/interviewEntryFlow'
import { getAuthToken } from '@/lib/auth'
import { getCurrentInterviewId } from '@/lib/storageHelpers'

/** Values stored on UserInterview.interview_type (DB source of truth). */
export type UserInterviewType =
  | 'resume_based'
  | 'jd_based'
  | 'system_design'
  | 'topic_based'
  | string

export type InterviewMetaResponse = {
  interview_id: string
  interview_type?: string | null
  status?: string | null
}

export type ActiveInterviewResponse = {
  interview_id?: string | null
  interview_type?: string | null
  status?: string | null
  attempt_number?: number | null
}

const RESUMABLE_INTERVIEW_STATUSES = new Set(['In Progress', 'Temp Abandoned'])

export function interviewTypeToEntryFlow(
  interviewType: string | null | undefined,
): InterviewEntryFlow | null {
  if (interviewType === 'resume_based') return 'resume'
  if (interviewType === 'jd_based') return 'jd'
  return null
}

function readCurrentInterview(): Record<string, unknown> | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = sessionStorage.getItem('currentInterview')
    if (!raw) return null
    const parsed = JSON.parse(raw) as Record<string, unknown>
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}

/** Prefer DB-backed type from start payload; fallback to legacy entry-flow flag. */
export function getInterviewTypeFromSession(): UserInterviewType | null {
  const current = readCurrentInterview()
  const fromStart = (
    current?.startInterviewData as { interview_type?: string } | undefined
  )?.interview_type
  if (fromStart) return fromStart

  const flow = getInterviewEntryFlow()
  if (flow === 'resume') return 'resume_based'
  if (flow === 'jd') return 'jd_based'
  return null
}

export function isResumeBasedInterviewType(
  interviewType: string | null | undefined,
): boolean {
  return interviewType === 'resume_based'
}

export function isJdBasedInterviewType(
  interviewType: string | null | undefined,
): boolean {
  return interviewType === 'jd_based'
}

/**
 * After /interview/start (or start-scheduled): mirror server interview_type into
 * session for UX. Does not affect server authorization.
 */
export function applyServerInterviewType(
  interviewType: string | null | undefined,
  interviewId?: string,
): void {
  if (typeof window === 'undefined' || !interviewType) return

  if (interviewId) {
    syncInterviewIdsInSession(String(interviewId))
  }

  const current = readCurrentInterview()
  const entryFlow = interviewTypeToEntryFlow(interviewType)
  if (entryFlow) setInterviewEntryFlow(entryFlow)

  if (!current) return

  const startInterviewData =
    (current.startInterviewData as Record<string, unknown> | undefined) ?? {}
  if (startInterviewData.interview_type === interviewType) return

  sessionStorage.setItem(
    'currentInterview',
    JSON.stringify({
      ...current,
      startInterviewData: { ...startInterviewData, interview_type: interviewType },
    }),
  )
}

export function interviewMetaPath(interviewId: string | number): string {
  return `/interview/${interviewId}/meta`
}

const _interviewMetaFetcher = createCoalescedFetcher<InterviewMetaResponse>(60_000)

/** Fetch authoritative type from API (for refresh / tamper reconcile).
 * Interview meta is immutable per interview, so successful results are briefly cached and
 * concurrent calls coalesced — this dedupes the several reads per page session
 * (ensure/reconcile paths) plus React StrictMode double-invoke. A no-token or non-OK
 * response returns null and is NOT cached (preserves the previous contract, allows retry). */
export async function fetchInterviewMeta(
  interviewId: string | number,
): Promise<InterviewMetaResponse | null> {
  const token = getAuthToken()
  if (!token) return null

  try {
    return await _interviewMetaFetcher(String(interviewId), async () => {
      const res = await fetch(`${API_BASE_URL}${interviewMetaPath(interviewId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error(`interview meta ${res.status}`)
      return (await res.json()) as InterviewMetaResponse
    })
  } catch {
    return null
  }
}

/**
 * If session entry-flow disagrees with API meta, overwrite session from API.
 * Returns resolved interview_type or null.
 */
export async function reconcileInterviewTypeWithApi(
  interviewId: string | number,
): Promise<UserInterviewType | null> {
  const meta = await fetchInterviewMeta(interviewId)
  if (!meta?.interview_type) {
    return getInterviewTypeFromSession()
  }

  const sessionType = getInterviewTypeFromSession()
  const sessionFlow = getInterviewEntryFlow()
  const apiFlow = interviewTypeToEntryFlow(meta.interview_type)

  if (
    sessionType !== meta.interview_type ||
    (apiFlow && sessionFlow && apiFlow !== sessionFlow)
  ) {
    applyServerInterviewType(meta.interview_type, String(interviewId))
  }

  return meta.interview_type
}

const _activeInterviewFetcher = createCoalescedFetcher<ActiveInterviewResponse | null>(0)

/** Fetch the caller's latest In Progress / Temp Abandoned row from the server.
 * Coalesces concurrent duplicate calls (entry-block hook + page effects, StrictMode
 * double-mount) into one in-flight request. No caching — later calls stay fresh. */
export function fetchActiveInterview(): Promise<ActiveInterviewResponse | null> {
  const token = getAuthToken()
  if (!token) return Promise.resolve(null)

  return _activeInterviewFetcher('active', async () => {
    const res = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ACTIVE_INTERVIEW}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) return null
    return (await res.json()) as ActiveInterviewResponse
  })
}

type InterviewIdSessionBlob = {
  interviewId?: string
  id?: string
  role?: string
  experience?: string
  topics?: string[]
}

function readInterviewIdSessionBlob(): InterviewIdSessionBlob | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = sessionStorage.getItem('interviewId')
    if (!raw) return null
    const parsed = JSON.parse(raw) as InterviewIdSessionBlob
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}

/** Keep `currentInterview.id` and `interviewId` session blob aligned (role/experience preserved). */
export function syncInterviewIdsInSession(interviewId: string): void {
  if (typeof window === 'undefined' || !interviewId) return

  const id = String(interviewId)
  const current = readCurrentInterview() ?? {}

  sessionStorage.setItem(
    'currentInterview',
    JSON.stringify({
      ...current,
      id,
    }),
  )

  const blob = readInterviewIdSessionBlob() ?? {}
  sessionStorage.setItem(
    'interviewId',
    JSON.stringify({
      ...blob,
      interviewId: id,
      id,
    }),
  )
}

/** Rewrite session id (and attempt) from server active-interview payload. */
export function applyActiveInterviewToSession(
  active: ActiveInterviewResponse,
): void {
  if (typeof window === 'undefined' || !active.interview_id) return

  const interviewId = String(active.interview_id)
  syncInterviewIdsInSession(interviewId)

  if (active.attempt_number != null && active.attempt_number > 0) {
    sessionStorage.setItem('currentAttemptNumber', String(active.attempt_number))
  }

  if (active.interview_type) {
    applyServerInterviewType(active.interview_type, interviewId)
  }
}

type JdInterviewSummaryResponse = {
  interview_id?: string | null
}

/**
 * Current non-completed JD-based interview for the signed-in user.
 * Used by JD entry links that carry no `jd_id` (the payment email is sent before a JD exists).
 */
export async function fetchCurrentJdInterviewId(): Promise<string | null> {
  if (!getAuthToken()) return null
  try {
    const response = await api.get<JdInterviewSummaryResponse>(
      API_ENDPOINTS.CURRENT_JD_INTERVIEW,
    )
    const id = response.data?.interview_id
    return id ? String(id) : null
  } catch {
    return null
  }
}

function isResumableMeta(meta: InterviewMetaResponse | null): boolean {
  return Boolean(meta?.status && RESUMABLE_INTERVIEW_STATUSES.has(meta.status))
}

/**
 * If session id is missing, meta 404, or meta is not resumable, recover from
 * GET /user/active-interview. Returns the interview id to use (if any).
 */


/** Reconcile session from server before /interview shell selection. */
export interface InterviewPageSession {
  interviewId: string | null
  /**
   * The server confirmed an interview already under way -- a resumable `meta.status`, or an
   * active interview for this caller. This is the authority the readiness gate reads, because
   * the sessionStorage heuristics can be wrong: a JD start response does not always carry the
   * fields they look for, which made a JD refresh replay the introduction while a mock one
   * resumed.
   */
  resumable: boolean
}

async function resolveSessionFromServer(): Promise<InterviewPageSession> {
  const interviewId = getCurrentInterviewId()

  if (interviewId) {
    const meta = await fetchInterviewMeta(interviewId)
    if (isResumableMeta(meta)) {
      const authoritativeId = String(meta?.interview_id ?? interviewId)
      syncInterviewIdsInSession(authoritativeId)
      if (meta?.interview_type) {
        await reconcileInterviewTypeWithApi(authoritativeId)
      }
      return { interviewId: authoritativeId, resumable: true }
    }
  }

  const active = await fetchActiveInterview()
  if (active?.interview_id) {
    applyActiveInterviewToSession(active)
    return { interviewId: String(active.interview_id), resumable: true }
  }

  return { interviewId: getCurrentInterviewId(), resumable: false }
}

export async function ensureInterviewSessionFromServer(): Promise<string | null> {
  const { interviewId } = await resolveSessionFromServer()
  return interviewId
}

export async function prepareInterviewPageSession(): Promise<InterviewPageSession> {
  const session = await resolveSessionFromServer()
  if (session.interviewId) {
    await reconcileInterviewTypeWithApi(session.interviewId)
  }
  return session
}
